import { AlertCircle, AlertTriangle, CheckCircle2, Clock, Loader2 } from "lucide-react";
import { useArtifactStalled } from "@/hooks/useProjects";
import { isArtifactEnriching } from "@/lib/artifacts";
import type { Artifact } from "@/services/projectsApi";

interface ArtifactStatusChipProps {
  artifact: Artifact;
  className?: string;
  /** Drop the states that mean everything is fine — "AI described". The review
   *  list wants them, because there the whole point is watching each row
   *  settle. A card sitting in the grid afterwards does not: the description is
   *  one click away, and a label on every tile saying nothing is wrong is just
   *  noise.
   *
   *  An artefact that finished with no description at all is still shown, since
   *  that is a real gap rather than a routine outcome — for a table as much as
   *  a picture, because nothing will retrieve it without one. */
  hideRoutine?: boolean;
}

/** Where an artifact has got to, in one line.
 *
 *  One component rather than three copies: the card, the preview dialog and the
 *  review list all have to answer the same question, and the rules are subtle
 *  enough that they had already drifted apart once — the card showed a stalled
 *  artifact as still working while the dialog showed it as done.
 *
 *  "AI described" is the counterpart of the green chip the document upload form
 *  shows when metadata lands. It means the same thing there and here: the model
 *  has finished and what you are looking at is its output, not yours. */
const ArtifactStatusChip = ({
  artifact,
  className = "",
  hideRoutine = false,
}: ArtifactStatusChipProps) => {
  // A hook, so it must be called before any branch returns.
  const stalled = useArtifactStalled(artifact);
  const base = `text-xs flex items-start gap-1.5 ${className}`;

  if (artifact.status === "Error") {
    return (
      <span className={`${base} text-destructive`} title={artifact.error ?? undefined}>
        <AlertCircle className="w-3.5 h-3.5 mt-[1px] shrink-0" />
        <span className="line-clamp-2">{artifact.error ?? "Could not be processed."}</span>
      </span>
    );
  }

  if (isArtifactEnriching(artifact)) {
    // Extraction itself finished — the file is stored and openable. Only the
    // description is outstanding, which is why this is a note and not a failure.
    if (stalled) {
      return (
        <span
          className={`${base} text-muted-foreground`}
          title="Extraction finished. Only the description is outstanding — extract again to retry it."
        >
          <AlertTriangle className="w-3.5 h-3.5 mt-[1px] shrink-0" />
          Description didn&apos;t finish
        </span>
      );
    }
    return artifact.status === "Processing" ? (
      <span className={`${base} text-blue-600`}>
        <Loader2 className="w-3.5 h-3.5 mt-[1px] shrink-0 animate-spin" />
        Describing…
      </span>
    ) : (
      <span className={`${base} text-muted-foreground`}>
        <Clock className="w-3.5 h-3.5 mt-[1px] shrink-0" />
        Waiting to be described
      </span>
    );
  }

  if (artifact.description) {
    if (hideRoutine) return null;
    return (
      <span className={`${base} text-green-600`}>
        <CheckCircle2 className="w-3.5 h-3.5 mt-[1px] shrink-0" />
        AI described
      </span>
    );
  }

  // Anything that settled without one is a gap worth seeing wherever it
  // appears, a table included: a table is summarised rather than split into
  // rows now, so no description means nothing will ever retrieve it.
  return <span className={`${base} text-muted-foreground`}>No description</span>;
};

export default ArtifactStatusChip;
